import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './UnifiedButton.module.css';

/**
 * UnifiedButton - Centralized button component for consistent styling across the app
 *
 * @param {string} variant - Button style: 'primary', 'secondary', 'danger', 'ghost', 'outline'
 * @param {string} size - Button size: 'small', 'medium', 'large'
 * @param {boolean} fullWidth - Whether button should take full width
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} loading - Whether button is in loading state
 * @param {boolean} inProgress - Alias for loading, matching legacy Button props
 * @param {string} type - Button type: 'button', 'submit', 'reset'
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {node} children - Button content
 * @param {node} icon - Optional icon element
 * @param {string} iconPosition - Icon position: 'left' or 'right'
 */
const UnifiedButton = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  loading = false,
  inProgress = false,
  type = 'button',
  onClick,
  className,
  children,
  icon,
  iconPosition = 'left',
  ...rest
}) => {
  const isLoading = loading || inProgress;
  const buttonClasses = classNames(
    css.button,
    css[variant],
    css[size],
    {
      [css.fullWidth]: fullWidth,
      [css.disabled]: disabled,
      [css.loading]: isLoading,
      [css.withIcon]: icon,
    },
    className
  );

  const handleClick = e => {
    if (!disabled && !isLoading && onClick) {
      onClick(e);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <span className={css.spinner}></span>
          <span className={css.loadingText}>{children}</span>
        </>
      );
    }

    if (icon) {
      return (
        <>
          {iconPosition === 'left' && <span className={css.icon}>{icon}</span>}
          <span>{children}</span>
          {iconPosition === 'right' && <span className={css.icon}>{icon}</span>}
        </>
      );
    }

    return children;
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || isLoading}
      {...rest}
    >
      {renderContent()}
    </button>
  );
};

UnifiedButton.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost', 'outline']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  inProgress: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  onClick: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
};

export default UnifiedButton;
